import { CompRef, VariantPointer } from '@wix/document-services-types';

import { EditorPointer, CompPointer } from '@wix/editor-document-logic-api';

type AsUniqueKeyConfig = { ignoreVariants?: boolean };

// TODO: these should be defined in a location that is shared by both EP repos, but not editors.
const $documentPointer = 'documentPointer'; // Symbol('Document Pointer');
const $innerElementPath = 'innerElementPath'; // Symbol('Inner elements path');

/**
 * Temporary copy-pasted from
 * https://github.com/wix-private/editor-platform-packages/blob/master/packages-services/editor-platform-editor-pointer-service/src/EditorPointerService.ts
 */
export class EditorPointerService {
  static namespace = 'EditorPointerService';

  private asUniqueKeyFunctions = new WeakMap<
    object,
    {
      cachedValue?: string;
      function: (docPointer: any, innerElementPath?: string[]) => string;
    }
  >();

  getDocumentPointer = <T extends CompPointer | CompRef>(
    editorPointer: EditorPointer<T>,
  ): T => editorPointer[$documentPointer];

  getInnerElementPath = (editorPointer: EditorPointer): string[] | undefined =>
    editorPointer?.[$innerElementPath];

  isInnerElement = <T = CompPointer | CompRef>(
    editorPointer: EditorPointer<T>,
  ): boolean => Boolean(editorPointer?.[$innerElementPath]);

  /**
   * NOTE – the implementation is changed
   */
  isSameEditorPointer = <
    T extends CompPointer | CompRef,
    K extends CompPointer | CompRef,
  >(
    editorPointerA: EditorPointer<T>,
    editorPointerB: EditorPointer<K>,
  ) => {
    return editorPointerA.asUniqueKey === editorPointerB.asUniqueKey;
  };

  buildEditorPointer = <T extends CompPointer | CompRef>(
    documentPointer: T,
    innerElementPath?: string[],
    asUniqueKey?: (docPointer: T, innerElementPath?: string[]) => string,
  ) => {
    const asUniqueKeyFunction = asUniqueKey || this.getEditorPointerUniqueId;
    const cache = this.asUniqueKeyFunctions;
    const pointer = {
      get asUniqueKey(): string {
        const entry = cache.get(this)!;
        if (entry.cachedValue !== undefined) {
          return entry.cachedValue;
        }
        entry.cachedValue = entry.function(
          this.documentPointer,
          this.innerElementPath,
        );
        return entry.cachedValue;
      },
      documentPointer,
      innerElementPath: innerElementPath?.length ? innerElementPath : undefined,
    };
    this.asUniqueKeyFunctions.set(pointer, {
      cachedValue: undefined,
      function: asUniqueKeyFunction,
    });
    return pointer;
  };

  buildChildEditorPointer = <T extends CompPointer | CompRef>(
    parentEditorPointer: EditorPointer<T>,
    childInnerElementPath: string[],
  ) => {
    const parentDocumentPointer = this.getDocumentPointer(parentEditorPointer);
    const combinedInnerElementPath = [
      ...(this.getInnerElementPath(parentEditorPointer) || []),
      ...childInnerElementPath,
    ];

    // Use the parent's asUniqueKey function for the child
    const parentAsUniqueKeyFunction =
      this.asUniqueKeyFunctions.get(parentEditorPointer)?.function;

    return this.buildEditorPointer(
      parentDocumentPointer,
      combinedInnerElementPath,
      parentAsUniqueKeyFunction,
    );
  };

  getEditorPointerUniqueId = <T extends CompPointer | CompRef>(
    documentPointer: T,
    innerElementPath?: string[],
    config?: AsUniqueKeyConfig,
  ) => {
    const UNIQUE_KEY_DELIMITER = '--';

    const getDocumentPointerUniqueId = (docPointer: CompPointer | CompRef) =>
      `${docPointer.type}--${docPointer.id}`;

    const getInnerElementPathId = (innerPath: string[]) => innerPath.join();

    const getVariantContextUniqueId = (
      variantContext: VariantPointer[],
    ): string =>
      variantContext
        .map((variantPointer) => variantPointer.id)
        .sort()
        .join();

    return (
      getDocumentPointerUniqueId(documentPointer) +
      (!config?.ignoreVariants && documentPointer.variants
        ? UNIQUE_KEY_DELIMITER +
          getVariantContextUniqueId(documentPointer.variants)
        : '') +
      (innerElementPath && innerElementPath.length > 0
        ? UNIQUE_KEY_DELIMITER + getInnerElementPathId(innerElementPath)
        : '')
    );
  };
}
